import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../lib/config';
import UserDetail from './UserDetail';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  Box,
  Chip,
  Avatar,
  Button,
  ButtonGroup,
  CircularProgress,
  Alert
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';

interface StandingsEntry {
  rank: number;
  user_id: string;
  username: string;
  display_name: string;
  wins: number;
  losses: number;
  pushes: number;
  win_percentage: string;
}

type SortField = 'rank' | 'display_name' | 'wins' | 'losses' | 'pushes' | 'win_percentage';
type SortDirection = 'asc' | 'desc';

interface LeaderboardProps {
  season: number;
  week?: number;
}

const Leaderboard: React.FC<LeaderboardProps> = ({ season, week }) => {
  const [standings, setStandings] = useState<StandingsEntry[]>([]);
  const [sortedStandings, setSortedStandings] = useState<StandingsEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [viewType, setViewType] = useState<'season' | 'week'>('season');
  const [sortField, setSortField] = useState<SortField>('rank');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [selectedUser, setSelectedUser] = useState<StandingsEntry | null>(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchStandings();
  }, [season, week, viewType]);

  useEffect(() => {
    sortStandings();
  }, [standings, sortField, sortDirection]);

  const sortStandings = () => {
    const sorted = [...standings].sort((a, b) => {
      let aValue: any = a[sortField];
      let bValue: any = b[sortField];

      if (sortField === 'win_percentage') {
        aValue = parseFloat(aValue);
        bValue = parseFloat(bValue);
      }

      if (sortField === 'display_name') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    setSortedStandings(sorted);
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection(field === 'rank' ? 'asc' : 'desc');
    }
  };


  const fetchStandings = async () => {
    try {
      setLoading(true);
      let url = '';
      
      if (viewType === 'week' && week) {
        url = `/.netlify/functions/standings/week/${season}/${week}`;
      } else {
        url = `/.netlify/functions/standings/season/${season}`;
      }

      const response = await fetch(`${API_BASE_URL}${url}`);
      if (!response.ok) throw new Error('Failed to fetch standings');
      
      const data = await response.json();
      setStandings(data);
      setSortedStandings(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getRankIcon = (rank: number) => {
    switch (rank) {
      case 1:
        return <EmojiEventsIcon sx={{ color: '#FFD700', fontSize: 20 }} />;
      case 2:
        return <EmojiEventsIcon sx={{ color: '#C0C0C0', fontSize: 20 }} />;
      case 3:
        return <EmojiEventsIcon sx={{ color: '#CD7F32', fontSize: 20 }} />;
      default:
        return null;
    }
  };

  const getWinPercentageColor = (percentage: string) => {
    const pct = parseFloat(percentage);
    if (pct >= 70) return 'success';
    if (pct >= 50) return 'warning';
    return 'error';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">Error: {error}</Alert>
      </Box>
    );
  }

  return (
    <Box p={3} maxWidth="1200px" margin="0 auto">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          🏆 Leaderboard - {season} Season
        </Typography>
        <ButtonGroup variant="contained" size="large">
          <Button
            onClick={() => setViewType('season')}
            variant={viewType === 'season' ? 'contained' : 'outlined'}
          >
            Season
          </Button>
          {week && (
            <Button
              onClick={() => setViewType('week')}
              variant={viewType === 'week' ? 'contained' : 'outlined'}
            >
              Week {week}
            </Button>
          )}
        </ButtonGroup>
      </Box>

      <TableContainer component={Paper} elevation={3}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={sortField === 'rank'}
                  direction={sortField === 'rank' ? sortDirection : 'asc'}
                  onClick={() => handleSort('rank')}
                >
                  <Typography variant="subtitle2" fontWeight="bold">
                    Rank
                  </Typography>
                  {sortField === 'rank' ? (
                    <Box component="span" sx={visuallyHidden}>
                      {sortDirection === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </Box>
                  ) : null}
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortField === 'display_name'}
                  direction={sortField === 'display_name' ? sortDirection : 'asc'}
                  onClick={() => handleSort('display_name')}
                >
                  <Typography variant="subtitle2" fontWeight="bold">
                    Player
                  </Typography>
                  {sortField === 'display_name' ? (
                    <Box component="span" sx={visuallyHidden}>
                      {sortDirection === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </Box>
                  ) : null}
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">
                <TableSortLabel
                  active={sortField === 'wins'}
                  direction={sortField === 'wins' ? sortDirection : 'desc'}
                  onClick={() => handleSort('wins')}
                >
                  <Typography variant="subtitle2" fontWeight="bold">
                    Wins
                  </Typography>
                  {sortField === 'wins' ? (
                    <Box component="span" sx={visuallyHidden}>
                      {sortDirection === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </Box>
                  ) : null}
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">
                <TableSortLabel
                  active={sortField === 'losses'}
                  direction={sortField === 'losses' ? sortDirection : 'asc'}
                  onClick={() => handleSort('losses')}
                >
                  <Typography variant="subtitle2" fontWeight="bold">
                    Losses
                  </Typography>
                  {sortField === 'losses' ? (
                    <Box component="span" sx={visuallyHidden}>
                      {sortDirection === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </Box>
                  ) : null}
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">
                <TableSortLabel
                  active={sortField === 'pushes'}
                  direction={sortField === 'pushes' ? sortDirection : 'asc'}
                  onClick={() => handleSort('pushes')}
                >
                  <Typography variant="subtitle2" fontWeight="bold">
                    Pushes
                  </Typography>
                  {sortField === 'pushes' ? (
                    <Box component="span" sx={visuallyHidden}>
                      {sortDirection === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </Box>
                  ) : null}
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">
                <TableSortLabel
                  active={sortField === 'win_percentage'}
                  direction={sortField === 'win_percentage' ? sortDirection : 'desc'}
                  onClick={() => handleSort('win_percentage')}
                >
                  <Typography variant="subtitle2" fontWeight="bold">
                    Win %
                  </Typography>
                  {sortField === 'win_percentage' ? (
                    <Box component="span" sx={visuallyHidden}>
                      {sortDirection === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </Box>
                  ) : null}
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedStandings.map((entry) => (
              <TableRow
                key={entry.user_id}
                onClick={() => setSelectedUser(entry)}
                sx={{
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'rgba(25, 118, 210, 0.08)',
                  },
                  '&:nth-of-type(odd)': {
                    backgroundColor: 'rgba(0, 0, 0, 0.02)',
                  },
                }}
              >
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    {getRankIcon(entry.rank)}
                    <Typography variant="h6" fontWeight="bold">
                      #{entry.rank}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Avatar sx={{ bgcolor: 'primary.main' }}>
                      {entry.display_name.charAt(0).toUpperCase()}
                    </Avatar>
                    <Box>
                      <Typography variant="body1" fontWeight="medium">
                        {entry.display_name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        @{entry.username}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell align="center">
                  <Chip
                    label={entry.wins}
                    color="success"
                    variant="outlined"
                    size="small"
                    sx={{ minWidth: 50, fontWeight: 'bold' }}
                  />
                </TableCell>
                <TableCell align="center">
                  <Chip
                    label={entry.losses}
                    color="error"
                    variant="outlined"
                    size="small"
                    sx={{ minWidth: 50, fontWeight: 'bold' }}
                  />
                </TableCell>
                <TableCell align="center">
                  <Chip
                    label={entry.pushes}
                    color="default"
                    variant="outlined"
                    size="small"
                    sx={{ minWidth: 50, fontWeight: 'bold' }}
                  />
                </TableCell>
                <TableCell align="center">
                  <Chip
                    label={`${entry.win_percentage}%`}
                    color={getWinPercentageColor(entry.win_percentage) as any}
                    variant="filled"
                    size="medium"
                    sx={{ minWidth: 70, fontWeight: 'bold' }}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {sortedStandings.length === 0 && (
          <Box p={4} textAlign="center">
            <Typography variant="h6" color="text.secondary">
              No standings data available for this period.
            </Typography>
          </Box>
        )}
      </TableContainer>

      {selectedUser && (
        <UserDetail
          userId={selectedUser.user_id}
          username={selectedUser.username}
          displayName={selectedUser.display_name}
          season={season}
          onClose={() => setSelectedUser(null)}
        />
      )}
    </Box>
  );
};

export default Leaderboard;